import type { Fetcher } from '../api/impl/utils/Fetcher';
import { AnalyticsError, AnalyticsEvent, PostAnalyticsFN } from '../common/Analytics';
import { retry } from '../utils/AsyncUtils';
import { ResponseError } from './ResponseError';

export function createPostAmplitudeAnalytics(
    fetcher: Fetcher,
    requestOpts?: { linearBackoff?: number; timeoutMs?: number },
): PostAnalyticsFN {
    return (events) => postAmplitudeAnalytics(fetcher, events, requestOpts);
}

async function postAmplitudeAnalytics(
    { fetch }: Fetcher,
    events: AnalyticsEvent[],
    requestOpts?: { linearBackoff?: number; timeoutMs?: number },
): Promise<void> {
    let amplitudeApiKey: string | undefined;
    try {
        amplitudeApiKey = process.env.AMPLITUDE;
    } catch {
        // Ignore errors on undefined `process.env` to handle builds without DefinePlugin
    }

    if (!amplitudeApiKey) {
        throw Error('Cannot send analytics: the AMPLITUDE environment variable is not defined');
    }

    // Include incremental `event_id` counter in events to ensure the ordering in Amplitude matches the tracking order:
    const eventsWithEventId = events.map((event, i) => ({ ...event, event_id: event.event_id ?? i }));

    const eventBatches = batchEvents(eventsWithEventId);

    for (const eventBatch of eventBatches) {
        const body = JSON.stringify({ api_key: amplitudeApiKey, events: eventBatch });

        try {
            await retry(
                () =>
                    fetch({
                        url: 'https://api.amplitude.com/2/httpapi',
                        method: 'POST',
                        body,
                        headers: { 'Content-Type': 'application/json' },
                        timeout: requestOpts?.timeoutMs ?? 5_000,
                    }).then(async (res) => {
                        if (res.statusCode < 200 || res.statusCode >= 400) {
                            throw await ResponseError.fromResponse('Amplitude API error response', res);
                        }
                    }),
                {
                    retries: 1,
                    retryOnlyIf: (error) => !(error instanceof ResponseError) || error.statusCode >= 500,
                    linearBackoff: requestOpts?.linearBackoff ?? 500,
                },
            );
        } catch (e: any) {
            throw new AnalyticsError(e.message, eventBatch);
        }
    }
}

/** Batch events to comply with Amplitude limits: https://www.docs.developers.amplitude.com/analytics/apis/http-v2-api/#upload-limit. */
function batchEvents(events: AnalyticsEvent[]): AnalyticsEvent[][] {
    const BATCH_MAX_EVENT_COUNT = 2000;
    const BATCH_MAX_BYTES = 950_000;

    const batches: { events: AnalyticsEvent[]; size: number }[] = [];

    for (const event of events) {
        let batch = batches.at(-1);
        const eventSize = JSON.stringify(event).length;

        if (!batch || batch.events.length >= BATCH_MAX_EVENT_COUNT || batch.size + eventSize >= BATCH_MAX_BYTES) {
            batch = { events: [], size: 0 };
            batches.push(batch);
        }

        batch.events.push(event);
        batch.size += eventSize;
    }

    return batches.map((batch) => batch.events);
}
